// Compression de la photo AVANT l'envoi (README « Architecture » : WebP 768 px, ~80 Ko).
//
// Deux raisons, toutes les deux mesurables : le coût en tokens d'entrée du modèle est proportionnel
// à la surface de l'image (au-delà de 768 px sur le grand côté il n'y a rien à gagner sur une photo
// de repas), et un téléphone en 4G téléverse 80 Ko instantanément là où 4 Mo prennent dix secondes.
//
// Le travail réel (décodage, canvas, encodage) est injecté (`DependancesCompression`) : la boucle
// de dégradation — qualités successives puis réduction du côté — est ainsi testable sous
// `node --test`, sans DOM (test/imageCompression.test.ts).

/** Grand côté visé, en pixels. Au-delà, le modèle ne gagne rien sur une photo de repas. */
export const COTE_MAX = 768;
/** Qualité WebP de départ. */
export const QUALITE_INITIALE = 0.8;
/** Taille visée : on s'arrête dès qu'on passe dessous. */
export const CIBLE_OCTETS = 80 * 1024;
/** Plafond dur : au-delà on continue à dégrader (critère d'acceptation : < 150 Ko envoyés). */
export const PLAFOND_OCTETS = 150 * 1024;

const QUALITES = [QUALITE_INITIALE, 0.65, 0.5, 0.4];
/** Facteurs appliqués au grand côté si les qualités ne suffisent pas (photo très texturée). */
const FACTEURS_COTE = [1, 0.75, 0.55];

export interface OptionsCompression {
  coteMax?: number;
  cibleOctets?: number;
  plafondOctets?: number;
}

/** Image décodée, réduite au strict nécessaire pour l'encodeur et pour la lecture de code-barres. */
export interface ImageDecodee {
  width: number;
  height: number;
  close?: () => void;
}

export interface DependancesCompression {
  /** Décode le fichier choisi (orientation EXIF appliquée) — `createImageBitmap` dans le navigateur. */
  decoder(fichier: Blob): Promise<ImageDecodee>;
  /** Ré-encode l'image aux dimensions demandées ; peut renvoyer un autre type que celui demandé. */
  encoder(image: ImageDecodee, largeur: number, hauteur: number, type: string, qualite: number): Promise<Blob>;
}

export interface PhotoCompressee {
  blob: Blob;
  largeur: number;
  hauteur: number;
  octets: number;
  type: string;
  qualite: number;
}

/**
 * Dimensions après réduction au grand côté `coteMax`, en conservant le rapport d'aspect.
 * Une image déjà plus petite n'est JAMAIS agrandie : ré-échantillonner vers le haut coûterait des
 * octets et des tokens sans ajouter un pixel d'information.
 */
export function dimensionsCibles(
  largeur: number,
  hauteur: number,
  coteMax: number = COTE_MAX,
): { largeur: number; hauteur: number } {
  if (!Number.isFinite(largeur) || !Number.isFinite(hauteur) || largeur <= 0 || hauteur <= 0) {
    throw new RangeError(`dimensions invalides (${largeur}x${hauteur})`);
  }
  const grandCote = Math.max(largeur, hauteur);
  if (grandCote <= coteMax) return { largeur: Math.round(largeur), hauteur: Math.round(hauteur) };
  const facteur = coteMax / grandCote;
  return {
    largeur: Math.max(1, Math.round(largeur * facteur)),
    hauteur: Math.max(1, Math.round(hauteur * facteur)),
  };
}

/**
 * Réduit et ré-encode la photo. On s'arrête au PREMIER candidat sous la cible ; si aucun n'y arrive,
 * on garde le plus petit obtenu — même au-dessus du plafond, l'envoi reste possible et l'appelant
 * décide (mieux vaut une photo lourde qu'aucune analyse).
 */
export async function compresserImage(
  fichier: Blob,
  deps: DependancesCompression,
  options: OptionsCompression = {},
): Promise<PhotoCompressee> {
  const coteMax = options.coteMax ?? COTE_MAX;
  const cible = options.cibleOctets ?? CIBLE_OCTETS;
  const plafond = options.plafondOctets ?? PLAFOND_OCTETS;

  const image = await deps.decoder(fichier);
  try {
    let meilleur: PhotoCompressee | null = null;

    for (const facteurCote of FACTEURS_COTE) {
      const { largeur, hauteur } = dimensionsCibles(image.width, image.height, Math.round(coteMax * facteurCote));
      for (const qualite of QUALITES) {
        const blob = await deps.encoder(image, largeur, hauteur, 'image/webp', qualite);
        const candidat: PhotoCompressee = {
          blob,
          largeur,
          hauteur,
          octets: blob.size,
          // `toBlob` d'un navigateur sans encodeur WebP (vieux Safari) rend un PNG sans prévenir :
          // le type retenu est celui du blob obtenu, jamais celui demandé.
          type: blob.type || 'image/webp',
          qualite,
        };
        if (candidat.octets <= cible) return candidat;
        if (!meilleur || candidat.octets < meilleur.octets) meilleur = candidat;
      }
      if (meilleur && meilleur.octets <= plafond) return meilleur;
    }

    if (!meilleur) throw new Error('compression impossible : aucun encodage produit');
    return meilleur;
  } finally {
    image.close?.();
  }
}

/** Dépendances réelles du navigateur : `createImageBitmap` + canvas (Offscreen si disponible). */
export function dependancesNavigateur(): DependancesCompression {
  return {
    async decoder(fichier) {
      // `imageOrientation: 'from-image'` : sans ça une photo prise en portrait sur iPhone arrive
      // couchée (l'orientation est dans l'EXIF, pas dans les pixels) et le modèle voit une assiette
      // de travers.
      return await createImageBitmap(fichier, { imageOrientation: 'from-image' });
    },
    async encoder(image, largeur, hauteur, type, qualite) {
      const canvas = creerCanvas(largeur, hauteur);
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
      if (!ctx) throw new Error('canvas 2d indisponible');
      ctx.drawImage(image as unknown as CanvasImageSource, 0, 0, largeur, hauteur);
      return await canvasVersBlob(canvas, type, qualite);
    },
  };
}

/**
 * Décode le fichier et en rend un `ImageData` pour le lecteur de code-barres (chemin zéro token,
 * tenté avant tout appel au modèle). Résolution volontairement plus haute que celle envoyée au
 * modèle : un EAN de 13 barres survit mal à 768 px.
 */
export async function imageDataDepuisFichier(fichier: Blob, coteMax = 1280): Promise<ImageData> {
  const bitmap = await createImageBitmap(fichier, { imageOrientation: 'from-image' });
  try {
    const { largeur, hauteur } = dimensionsCibles(bitmap.width, bitmap.height, coteMax);
    const canvas = creerCanvas(largeur, hauteur);
    const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
    if (!ctx) throw new Error('canvas 2d indisponible');
    ctx.drawImage(bitmap, 0, 0, largeur, hauteur);
    return ctx.getImageData(0, 0, largeur, hauteur);
  } finally {
    bitmap.close();
  }
}

function creerCanvas(largeur: number, hauteur: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(largeur, hauteur);
  const canvas = document.createElement('canvas');
  canvas.width = largeur;
  canvas.height = hauteur;
  return canvas;
}

async function canvasVersBlob(
  canvas: HTMLCanvasElement | OffscreenCanvas,
  type: string,
  qualite: number,
): Promise<Blob> {
  if (canvas instanceof OffscreenCanvas) return await canvas.convertToBlob({ type, quality: qualite });
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("l'encodage de la photo a échoué"))),
      type,
      qualite,
    );
  });
}

/** « 78 Ko » — pour dire à l'utilisateur (et au relecteur) ce qui est réellement parti sur le réseau. */
export function formaterOctets(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  return `${Math.round(octets / 1024)} Ko`;
}
